import React, { useState } from 'react';
import { Question, Category } from '../../types';
import { generateQuestionsAI } from '../../services/geminiService';

interface AdminAIGeneratorProps {
    categories?: Category[];
}

const AdminAIGenerator: React.FC<AdminAIGeneratorProps> = ({ categories = [] }) => {
    // Basic Fields
    const [subject, setSubject] = useState('');
    const [topic, setTopic] = useState('');
    const [count, setCount] = useState(5);
    const [difficulty, setDifficulty] = useState('Medium');

    // Advanced Fields
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [examType, setExamType] = useState('UPSC');
    const [language, setLanguage] = useState('English');
    const [questionStyle, setQuestionStyle] = useState('conceptual');
    const [includeCurrentAffairs, setIncludeCurrentAffairs] = useState(false);
    const [yearRange, setYearRange] = useState('');
    const [specificInstructions, setSpecificInstructions] = useState('');

    // State
    const [loading, setLoading] = useState(false);
    const [generatedQuestions, setGeneratedQuestions] = useState<Question[]>([]);
    const [error, setError] = useState('');
    const [retryCount, setRetryCount] = useState(0);

    const handleGenerate = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setGeneratedQuestions([]);

        // Build enhanced prompt with all options
        const advancedOptions = showAdvanced ? {
            examType,
            language,
            questionStyle,
            includeCurrentAffairs,
            yearRange,
            specificInstructions
        } : null;

        try {
            const questions = await generateQuestionsAI(
                subject,
                topic,
                count,
                difficulty,
                advancedOptions
            );
            setGeneratedQuestions(questions);
            setRetryCount(0);
        } catch (err: any) {
            const errorMsg = err.message || 'Failed to generate questions';

            // Better error handling for JSON parse errors
            if (errorMsg.includes('JSON') || errorMsg.includes('parse') || errorMsg.includes('Unexpected')) {
                setError(`⚠️ AI response was malformed. This can happen with complex requests. Try reducing the question count or simplifying the topic.`);
            } else if (errorMsg.includes('API_KEY') || errorMsg.includes('apiKey')) {
                setError('❌ API Key not configured. Please set GEMINI_API_KEY in environment variables.');
            } else if (errorMsg.includes('quota') || errorMsg.includes('limit')) {
                setError('⏳ API rate limit reached. Please wait a moment and try again.');
            } else {
                setError(`❌ ${errorMsg}`);
            }
            setRetryCount(prev => prev + 1);
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadCSV = () => {
        const headers = ['stem', 'option_a', 'option_b', 'option_c', 'option_d', 'correct', 'explanation', 'difficulty', 'subject'];
        const rows = generatedQuestions.map(q => [
            `"${q.text.replace(/"/g, '""')}"`,
            `"${q.options[0]?.text.replace(/"/g, '""') || ''}"`,
            `"${q.options[1]?.text.replace(/"/g, '""') || ''}"`,
            `"${q.options[2]?.text.replace(/"/g, '""') || ''}"`,
            `"${q.options[3]?.text.replace(/"/g, '""') || ''}"`,
            q.correctOptionId,
            `"${q.explanation.replace(/"/g, '""')}"`,
            q.difficulty,
            `"${(q.subject || '').replace(/"/g, '""')}"`
        ]);
        const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `questions_${topic.replace(/\s+/g, '_')}_${Date.now()}.csv`;
        a.click();
    };

    const handleCopyJSON = () => {
        const json = JSON.stringify(generatedQuestions, null, 2);
        navigator.clipboard.writeText(json);
        alert('Questions copied to clipboard as JSON!');
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
                <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700">
                    {/* Header */}
                    <div className="flex items-center mb-6">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white mr-3">
                            <i className="fas fa-robot"></i>
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-slate-800 dark:text-white">AI Generator</h3>
                            <p className="text-xs text-slate-500 dark:text-slate-400">Powered by Gemini 2.5 Flash</p>
                        </div>
                    </div>

                    <form onSubmit={handleGenerate} className="space-y-4">
                        {/* Basic Fields */}
                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                Subject <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                placeholder="e.g. Indian Polity, Geography, History"
                                className="w-full p-2.5 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                Specific Topic <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={topic}
                                onChange={(e) => setTopic(e.target.value)}
                                placeholder="e.g. Fundamental Rights, Monsoon Pattern"
                                className="w-full p-2.5 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400"
                                required
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Difficulty</label>
                                <select
                                    value={difficulty}
                                    onChange={(e) => setDifficulty(e.target.value)}
                                    className="w-full p-2.5 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                                >
                                    <option>Easy</option>
                                    <option>Medium</option>
                                    <option>Hard</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Count</label>
                                <input
                                    type="number"
                                    min="1"
                                    max="25"
                                    value={count}
                                    onChange={(e) => setCount(parseInt(e.target.value) || 5)}
                                    className="w-full p-2.5 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                                />
                            </div>
                        </div>

                        {/* Advanced Options Toggle */}
                        <button
                            type="button"
                            onClick={() => setShowAdvanced(!showAdvanced)}
                            className="w-full flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors"
                        >
                            <span className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center">
                                <i className={`fas fa-sliders-h mr-2 text-purple-500`}></i>
                                Advanced Options
                            </span>
                            <i className={`fas fa-chevron-${showAdvanced ? 'up' : 'down'} text-slate-400`}></i>
                        </button>

                        {/* Advanced Fields */}
                        {showAdvanced && (
                            <div className="space-y-4 p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg border border-slate-200 dark:border-slate-600">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Exam Type</label>
                                        <select
                                            value={examType}
                                            onChange={(e) => setExamType(e.target.value)}
                                            className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-purple-500 outline-none bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                                        >
                                            <option>UPSC</option>
                                            <option>APPSC</option>
                                            <option>SSC</option>
                                            <option>Banking</option>
                                            <option>Railways</option>
                                            <option>State PSC</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Language</label>
                                        <select
                                            value={language}
                                            onChange={(e) => setLanguage(e.target.value)}
                                            className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-purple-500 outline-none bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                                        >
                                            <option>English</option>
                                            <option>Hindi</option>
                                            <option>Telugu</option>
                                            <option>Bilingual (Eng+Hindi)</option>
                                        </select>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Question Style</label>
                                    <select
                                        value={questionStyle}
                                        onChange={(e) => setQuestionStyle(e.target.value)}
                                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-purple-500 outline-none bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm"
                                    >
                                        <option value="conceptual">Conceptual (Understanding)</option>
                                        <option value="factual">Factual (Memory-based)</option>
                                        <option value="analytical">Analytical (Application)</option>
                                        <option value="pyq">Previous Year Style (PYQ)</option>
                                        <option value="mixed">Mixed (All Types)</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Year Range (for Current Affairs)</label>
                                    <input
                                        type="text"
                                        value={yearRange}
                                        onChange={(e) => setYearRange(e.target.value)}
                                        placeholder="e.g. 2023-2024 or December 2024"
                                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-purple-500 outline-none bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm placeholder-slate-400"
                                    />
                                </div>

                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="includeCA"
                                        checked={includeCurrentAffairs}
                                        onChange={(e) => setIncludeCurrentAffairs(e.target.checked)}
                                        className="w-4 h-4 text-purple-600 border-slate-300 rounded focus:ring-purple-500"
                                    />
                                    <label htmlFor="includeCA" className="ml-2 text-sm text-slate-700 dark:text-slate-300">
                                        Include Current Affairs Context
                                    </label>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Special Instructions</label>
                                    <textarea
                                        value={specificInstructions}
                                        onChange={(e) => setSpecificInstructions(e.target.value)}
                                        placeholder="e.g. Focus on recent amendments, Include map-based questions, Add statement-based MCQs"
                                        rows={3}
                                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded focus:ring-2 focus:ring-purple-500 outline-none bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm placeholder-slate-400 resize-none"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Error Display */}
                        {error && (
                            <div className="p-3 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-sm rounded-lg border border-red-100 dark:border-red-800">
                                {error}
                                {retryCount > 0 && retryCount < 3 && (
                                    <p className="mt-2 text-xs">Tip: Try reducing count to {Math.max(3, count - 2)} or simplify the topic.</p>
                                )}
                            </div>
                        )}

                        {/* Generate Button */}
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white py-3 rounded-lg font-medium transition-all disabled:opacity-70 flex items-center justify-center shadow-lg"
                        >
                            {loading ? (
                                <><i className="fas fa-circle-notch fa-spin mr-2"></i> Generating...</>
                            ) : (
                                <><i className="fas fa-magic mr-2"></i> Generate Questions</>
                            )}
                        </button>
                    </form>
                </div>
            </div>

            {/* Output Panel */}
            <div className="lg:col-span-2">
                <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 h-full flex flex-col">
                    <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50 flex justify-between items-center">
                        <div>
                            <h3 className="font-bold text-slate-800 dark:text-white">Generated Output</h3>
                            {generatedQuestions.length > 0 && (
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                    {generatedQuestions.length} questions generated
                                </p>
                            )}
                        </div>
                        {generatedQuestions.length > 0 && (
                            <div className="flex gap-2">
                                <button
                                    onClick={handleCopyJSON}
                                    className="text-sm bg-slate-600 hover:bg-slate-700 text-white px-3 py-1.5 rounded-lg flex items-center transition-colors"
                                >
                                    <i className="fas fa-copy mr-2"></i> Copy JSON
                                </button>
                                <button
                                    onClick={handleDownloadCSV}
                                    className="text-sm bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg flex items-center transition-colors"
                                >
                                    <i className="fas fa-download mr-2"></i> Download CSV
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="flex-1 p-6 overflow-y-auto max-h-[650px] bg-slate-50/50 dark:bg-slate-900/30">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center h-64 text-slate-400">
                                <div className="w-16 h-16 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mb-4"></div>
                                <p className="text-slate-600 dark:text-slate-400">Generating {count} questions...</p>
                                <p className="text-xs text-slate-400 mt-2">This may take 10-30 seconds</p>
                            </div>
                        ) : generatedQuestions.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-64 text-slate-400">
                                <i className="fas fa-robot text-5xl mb-4 text-slate-300 dark:text-slate-600"></i>
                                <p className="text-slate-600 dark:text-slate-400">AI generated questions will appear here.</p>
                                <p className="text-xs text-slate-400 mt-2">Fill the form and click Generate</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {generatedQuestions.map((q, idx) => (
                                    <div key={idx} className="bg-white dark:bg-slate-800 p-4 rounded-lg border border-slate-200 dark:border-slate-700 text-sm shadow-sm">
                                        <div className="flex justify-between mb-3">
                                            <span className="font-bold text-slate-800 dark:text-white">Q{idx + 1}. {q.text}</span>
                                            <div className="flex gap-2">
                                                <span className={`text-xs px-2 py-1 rounded font-medium ${q.difficulty === 'Easy' ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400' :
                                                        q.difficulty === 'Hard' ? 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400' :
                                                            'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-400'
                                                    }`}>{q.difficulty}</span>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
                                            {q.options.map(opt => (
                                                <div key={opt.id} className={`px-3 py-2 rounded-lg border ${opt.id === q.correctOptionId
                                                    ? 'bg-green-50 border-green-300 text-green-800 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                                                    : 'bg-slate-50 border-slate-200 text-slate-700 dark:bg-slate-700 dark:border-slate-600 dark:text-slate-300'
                                                    }`}>
                                                    <span className="font-bold mr-2">{opt.id})</span> {opt.text}
                                                    {opt.id === q.correctOptionId && <i className="fas fa-check ml-2 text-green-600"></i>}
                                                </div>
                                            ))}
                                        </div>
                                        <div className="text-slate-600 dark:text-slate-400 text-xs bg-slate-50 dark:bg-slate-700/50 p-3 rounded-lg">
                                            <span className="font-semibold text-slate-700 dark:text-slate-300">💡 Explanation:</span> {q.explanation}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminAIGenerator;
